import React, { useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { toast } from "sonner";
import { Layout } from "../components/tag/Layout";
import { HeroBg, EyebrowPill, SectionPill } from "../components/tag/Primitives";
import { TOOLS, TOOL_LIST, interpret } from "../data/tools";
import { api } from "../lib/api";

export const ToolsIndex = () => (
  <Layout title="Free Authority Tools | The Authority Gap" path="/tools">
    <section style={{ background: "#090909", padding: "70px 32px 40px", position: "relative", overflow: "hidden", textAlign: "center" }}>
      <HeroBg />
      <div style={{ position: "relative", zIndex: 1, maxWidth: 720, margin: "0 auto" }}>
        <div style={{ marginBottom: 22 }}><EyebrowPill>Free Tools</EyebrowPill></div>
        <h1 className="tag-h1" style={{ marginBottom: 14 }}>Diagnose your authority gap.</h1>
        <p style={{ color: "#aaa", fontSize: 15 }}>Five free assessments. Each takes under 3 minutes and produces a personalized report.</p>
      </div>
    </section>
    <section style={{ background: "#090909", borderTop: "1px solid #1a1a1a", padding: "40px 20px 64px" }}>
      <div style={{ maxWidth: 1000, margin: "0 auto", display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 12 }} className="tag-tools-grid">
        <ToolCard
          slug="score"
          title="Authority Gap Score"
          blurb="The flagship 7-question assessment. Score 0–100 + biggest gap + recommended next step."
          href="/score"
          minutes={3}
          featured
        />
        {TOOL_LIST.map((t) => (
          <ToolCard key={t.slug} slug={t.slug} title={t.name} blurb={t.blurb} href={`/tools/${t.slug}`} minutes={t.estMinutes} />
        ))}
      </div>
      <style>{`@media (max-width: 760px){ .tag-tools-grid{ grid-template-columns: 1fr !important; } }`}</style>
    </section>
  </Layout>
);

const ToolCard = ({ slug, title, blurb, href, minutes, featured }) => (
  <Link to={href} data-testid={`tool-card-${slug}`} className="tag-lab-card" style={{ display: "block", textDecoration: "none" }}>
    {featured && <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 2, background: "#FF6575" }} />}
    <div style={{ fontSize: 9, color: "#888", textTransform: "uppercase", letterSpacing: "0.1em", fontWeight: 700, marginBottom: 8 }}>
      {featured ? "Flagship" : "Free Tool"} · {minutes} min
    </div>
    <h3 style={{ color: "#fff", fontSize: 16, fontWeight: 700, margin: "0 0 8px" }}>{title}</h3>
    <p style={{ color: "#bbb", fontSize: 12, lineHeight: 1.6, margin: 0 }}>{blurb}</p>
    <div style={{ marginTop: 14, color: "#FF6575", fontSize: 12, fontWeight: 600 }}>Start →</div>
  </Link>
);

export default function ToolPage() {
  const { slug } = useParams();
  const tool = TOOLS[slug];
  const [answers, setAnswers] = useState({});
  const [step, setStep] = useState(0);
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  if (!tool) return <Navigate to="/tools" replace />;

  const total = tool.questions.length + 1;
  const progress = Math.round(((step + (result ? 1 : 0)) / total) * 100);

  const setAns = (k, v) => setAnswers((a) => ({ ...a, [k]: v }));

  const score = Object.values(answers).reduce((s, n) => s + (typeof n === "number" ? n : 0), 0);
  const interp = interpret(Math.min(100, score));

  const next = () => setStep((s) => s + 1);
  const back = () => setStep((s) => Math.max(0, s - 1));

  const submit = async (e) => {
    e.preventDefault();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return toast.error("Please enter a valid email.");
    setSubmitting(true);
    const finalScore = Math.min(100, score);
    const biggestGap = tool.biggestGap(answers);
    const nextStep = tool.nextStep(finalScore);
    try {
      await api.post("/tools/submit", {
        slug: tool.slug, answers, score: finalScore,
        biggest_gap: biggestGap, next_step: nextStep, email,
      });
      setResult({ score: finalScore, biggest_gap: biggestGap, next_step: nextStep });
    } catch {
      toast.error("Could not submit. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const isEmailStep = step === tool.questions.length;

  return (
    <Layout title={`${tool.name} | The Authority Gap`} path={`/tools/${slug}`}>
      <section style={{ background: "#090909", padding: "60px 20px 32px", position: "relative", overflow: "hidden", textAlign: "center" }}>
        <HeroBg />
        <div style={{ position: "relative", zIndex: 1, maxWidth: 720, margin: "0 auto" }}>
          <div style={{ marginBottom: 18 }}><SectionPill>{tool.eyebrow}</SectionPill></div>
          <h1 className="tag-h1" style={{ marginBottom: 12, fontSize: 36 }}>{tool.name}</h1>
          <p style={{ color: "#aaa", fontSize: 15 }}>{tool.blurb}</p>
        </div>
      </section>

      <section style={{ background: "#090909", borderTop: "1px solid #1a1a1a", padding: "32px 20px 64px" }}>
        <div className="tag-card" data-testid={`tool-${slug}-container`} style={{ maxWidth: 560, margin: "0 auto", padding: 32 }}>
          {!result ? (
            <>
              <div style={{ height: 3, background: "#1a1a1a", borderRadius: 2, marginBottom: 20, overflow: "hidden" }}>
                <div style={{ height: "100%", width: `${progress}%`, background: "#FF6575", transition: "width 0.3s ease" }} />
              </div>
              <div style={{ color: "#777", fontSize: 11, marginBottom: 16 }}>Step {step + 1} of {total}</div>

              {!isEmailStep ? (
                <>
                  <h3 style={{ color: "#fff", fontSize: 16, fontWeight: 700, margin: "0 0 16px" }}>{tool.questions[step].q}</h3>
                  {tool.questions[step].options.map((opt) => {
                    const selected = answers[tool.questions[step].key] === opt.v;
                    return (
                      <button
                        type="button"
                        key={opt.l}
                        data-testid={`tool-opt-${opt.l.slice(0, 14).replace(/\W/g, "")}`}
                        onClick={() => setAns(tool.questions[step].key, opt.v)}
                        className={`tag-option ${selected ? "selected" : ""}`}
                      >
                        <span className="tag-radio-dot" />
                        {opt.l}
                      </button>
                    );
                  })}
                  <div style={{ display: "flex", gap: 10, marginTop: 18 }}>
                    {step > 0 && <button type="button" className="btn-ghost" onClick={back}>Back</button>}
                    <button
                      type="button"
                      className="btn-primary"
                      onClick={next}
                      disabled={answers[tool.questions[step].key] === undefined}
                      data-testid="tool-next"
                    >
                      Next
                    </button>
                  </div>
                </>
              ) : (
                <form onSubmit={submit}>
                  <h3 style={{ color: "#fff", fontSize: 16, fontWeight: 700, margin: "0 0 6px" }}>Where should we send your report?</h3>
                  <p style={{ color: "#888", fontSize: 11, margin: "0 0 16px" }}>No spam. Unsubscribe any time.</p>
                  <input
                    data-testid="tool-email"
                    type="email"
                    className="tag-input"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    style={{ marginBottom: 14 }}
                  />
                  <div style={{ display: "flex", gap: 10 }}>
                    <button type="button" className="btn-ghost" onClick={back}>Back</button>
                    <button data-testid="tool-submit" type="submit" className="btn-primary" disabled={submitting} style={{ flex: 1 }}>
                      {submitting ? "Calculating…" : "Get my report"}
                    </button>
                  </div>
                </form>
              )}
            </>
          ) : (
            <ToolResult tool={tool} result={result} interp={interpret(result.score)} email={email} />
          )}
        </div>
      </section>
    </Layout>
  );
}

const ToolResult = ({ tool, result, interp, email }) => (
  <div data-testid="tool-result">
    <div style={{ fontSize: 64, fontWeight: 700, color: "#fff", textAlign: "center", letterSpacing: "-0.03em", lineHeight: 1, marginBottom: 6 }}>{result.score}</div>
    <div style={{ color: "#888", fontSize: 11, textTransform: "uppercase", letterSpacing: "0.08em", textAlign: "center", marginBottom: 14 }}>{tool.name}</div>
    <div style={{ textAlign: "center", marginBottom: 18 }}>
      <span style={{ display: "inline-block", padding: "6px 14px", background: interp.bg, color: interp.color, borderRadius: 20, fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em" }}>{interp.label}</span>
    </div>
    <Box label="YOUR BIGGEST GAP">{result.biggest_gap}</Box>
    <Box label="RECOMMENDED NEXT STEP">{result.next_step}</Box>
    <Link to="/audit" className="btn-primary" style={{ display: "block", textAlign: "center", width: "100%", marginTop: 6 }}>Get an Authority Audit — $49</Link>
    <p style={{ color: "#777", fontSize: 11, textAlign: "center", marginTop: 10 }}>
      We sent a copy to <span style={{ color: "#aaa" }}>{email}</span>.
    </p>
  </div>
);

const Box = ({ label, children }) => (
  <div className="tag-card" style={{ marginBottom: 10, padding: 18 }}>
    <div style={{ color: "#FF6575", fontSize: 9, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 8 }}>{label}</div>
    <p style={{ color: "#bbb", fontSize: 13, lineHeight: 1.6, margin: 0 }}>{children}</p>
  </div>
);
